import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppService, AppUtilityService, AppSettingService } from '../app.service';
import { MapAPIService } from '../map/map-api.service';
import { MapService } from '../map/map.service';
import { ResourceAPIService, IRolePermit } from '../resource/resource.service';
import { ESocialSourceType, ESocialType, ESocialIconType } from '../socialObject/social-object.service';
import { IMilitary } from "../military/military-api.service";

export enum ETerritoryCardDisplayMode {
	Auto = 0,
	Mobile = 1, // always moble
	Desktop = 2 // always desktop
}

export enum ETerritoryCardLearnMoreType {
	LearnMore = 1,
	Audio = 2,
	PodcastEpisode = 3	
}

export enum ETerritoryCardType {
	Career = 1,
	Certification = 2,
	CTE = 3,
	Military = 4,
	College = 5,
	K12 = 6,
	PreK = 7,
	Degree = 1000, /*used in map, the waypoint type is degree, not sure exactly the type*/
	Resource = 1001,
	Institution = 1002,
	FieldsOfStudy = 1003,
	CSCDegree = 1004,
	Podcast = 1005,
	PodcastEpisode = 1006,
	NCES = 1007,
	Training = 1008,
	CertificateOrg = 1009,
	ProfessionalAssociation = 1010,
	Folder = 2000
}

export interface IMapInstance {
	ClusterId: string,
	ClusterHeroImage: string,
	ClusterName: string,
	MapObjectId: string,
	PathwayId: string,
	PathwayHeroImage: string,
	PathwayName: string,
	ClusterIcon: string,
	// the following prpoerties used by "field of study" in angular only
	ClusterDescription: string,
	PathwayDescription: string,
	CIPCode: string,
	NumberOfGraduation: string,
	MedianTotalDebt: string,
	MedianEarnings: string,
	MonthlyLoanPayment: string,
	MonthlyEarnings: string
}

export interface IAtAGlanceMilitary {
	MOSCode: string,
	MCId: string,
	Branch: string,
	BranchIcon: string
	MOSUrl: string,
	MCUrl: string
}

// please use lowercase at begin for all "local" variables.
// most variables fro API has uppercase at begin
export interface ITerritoryCard {
	tCardType: ETerritoryCardType,
	objId: string, // required for any card
	HasLearnMore: boolean,
	learnMoreType: ETerritoryCardLearnMoreType,
	disableLearnMore: boolean,
	Journeyable: boolean,
	CanSocial: boolean,
	Name: string,
	Description: string,
	hasVideo: boolean,
	hasSelect: boolean,
	externalUrl: string,
	onMap: boolean,
	hasAtAGlance: boolean,
	hasMany: boolean,
	ObjectInstances: IMapInstance[],
	OccupationCode: string,
	CIPCode: string,
	MapObjectId: string,
	ancestoryIcons: any,
	ancestoryIconTitle: string,
	ancestoryBreadCrumb: string,
	TerritoryId: number;
	HeroImage: string,
	Depth: number;
	socialSource: ESocialSourceType,
	socialType: ESocialType,
	socialIcon: ESocialIconType,
	socialKey: string,
	socialObject: any,  //used to store object from external, such as resource, certificate org
	//at a glance
	ClusterTotal: string,
	PathwayTotal: string,
	SubjectsTotal: number,  //k12
	StrandTotal: number, //k12, CTE
	StandardTotal: number, //k12, CTE
	Benchmark: number, //k12
	SubBenchmark: number,//k12
	PerformanceIndicator: number, //CTE
	OccupationTotal: string, // certification, college, career
	ProgramTotal: string,
	CourseTotal: string, 
	AverageAnnualSalary: string, 
	ClassLength: number,
	CreditTotal: number,
	ProfessorTotal: string,
	Education: any,
	// occupation
	SkillTotal: number,
	AbilityTotal: number,
	KnowledgeTotal: number,
	TechnologyTotal: number,
	TaskTotal: number,
	WorkActivitiyTotal: number,
	ProjectedEmploymentGrowthAverage: string,
	ProjectedEmploymentGrowthPercent: string
	ProjectedJobOpenings: string,
	CurrentEmployment: string,
	OccupationName: string
	LevelIconClusters: any
	LevelIconPathway: any,
	FitScoreInterest: number,
	FitScoreSkill: number,
	FitScoreSum: number,
	AtAGlance_College: {
		MedianEarnings: string,
		MedianTotalDebt: string,
		MonthlyEarnings: string,
		MonthlyLoanPayment: string,
		NumGraduates: string
	},
	AtAGlance_Institution: {
		SchoolYear: string,
		Type: string,
		StudentSize: number,
		Locale: string,
		Classroom: string,
		spaceHolderImage: string,
	},
	AtAGlance_CSCDegree: {
		NumberOfGraduation: number,
		MedianTotalDebt: string,
		MedianEarnings: string,
		MonthlyLoanPayment: string,
		MonthlyEarnings: string
	},
	AtAGlance_Podcast: {
		ArtistName: string
		ReleaseDate: string
	},
	AtAGlance_PodcastEpisode: {
		TrackTime: string
		ReleaseDate: string,
		Rating: string,
		EpisodeContentType: string,
		EpisodeFileExtension: string,
		EpisodeUrl: string
	},
	AtAGlance_Training: {
		OriginalData: any,
		SchoolName: string
		City: string,
		State: string,
		ZipCode: string,
		Phone: string,
		ProgramLength: string[]
	}
	AtAGlance_Certification: {
		CertifyingOrganizationTotal: string,
		CertificateTotal: string,
		TrainingTotal: string,
		ResourceTotal: string,
		EducationTrainingRequired?: string,
		WorkExperienceRequired?: string,
		OralWrittenExamRequired?: string,
		CertificateRenewal?: string,
		CertificateReexam?: string,
		OrgName: string,
		OrgUrl: string, 
		CertificateUrl: string
	},
	AtAGlance_Resource: {
		CanEdit: boolean,
		SetVisibility: boolean,
		Share: boolean,
		Priority: boolean,
		IsFile: boolean,
		PersonRole: string
		PersonId: string,
		CreatedTime: string
	}
	Institutions: any
	Folder: IFolder,
	AtAGlance_Military: IAtAGlanceMilitary
};

export interface IFolder {
	FolderId: string,
	FolderName: string,
	CreatedDate: string,
	CanDelete: boolean,
	ContainsItems: boolean,
	ContainsNumTopLevelFolders: number,
	ContainsNumTopLevelFiles: number,
}

export interface IProfessionalAssociation {
	Name: string,
	Url: string,
	HeroImage: string
}
export class TerritoryCardData implements ITerritoryCard {
	constructor() { }

	tCardType: ETerritoryCardType = null;
	objId: string = null;
	HasLearnMore = false;
	learnMoreType = ETerritoryCardLearnMoreType.LearnMore;
	disableLearnMore = false;
	Journeyable = false;
	CanSocial = false;
	Name: string = null;
	Description: string = null;
	hasVideo = false;
	hasSelect = false;
	externalUrl = "";
	onMap = false;
	hasAtAGlance = false;
	hasMany = false; // has many instances
	ObjectInstances: IMapInstance[] = null;	
	OccupationCode: string = null;
	CIPCode: string = null;
	MapObjectId: string = null;
	ancestoryIcons: any = null;
	ancestoryBreadCrumb: any = null;
	ancestoryIconTitle = "";
	TerritoryId: number = null;
	HeroImage: string = null;
	Depth: number = null;
	socialSource: ESocialSourceType = null;
	socialType = ESocialType.Unknown;
	socialIcon = ESocialIconType.Follow;
	socialKey: string = null;
	socialObject:  any = null;
	//at a glance
	ClusterTotal: string = null;
	PathwayTotal: string = null;
	SubjectsTotal: number = null;
	StrandTotal: number = null;
	StandardTotal: number = null;
	Benchmark: number = null;
	SubBenchmark: number = null;
	PerformanceIndicator: number = null;
	OccupationTotal: string = null;
	ProgramTotal: string = null;
	CourseTotal: string = null;
	AverageAnnualSalary: string = null;
	ClassLength: number = null;
	CreditTotal: number = null;
	ProfessorTotal: string = null;
	Education: any = null;
	// occupation
	SkillTotal: number = null;
	AbilityTotal: number = null;
	KnowledgeTotal: number = null;
	TechnologyTotal: number = null;
	TaskTotal: number = null;
	WorkActivitiyTotal: number = null;
	ProjectedEmploymentGrowthAverage: string = null;
	ProjectedEmploymentGrowthPercent: string = null;
	ProjectedJobOpenings: string = null;
	CurrentEmployment: string = null;
	OccupationName: string = null;
	LevelIconClusters: any = null;
	LevelIconPathway: any = null;
	FitScoreInterest: number = null;
	FitScoreSkill: number = null;
	FitScoreSum: number = null;
	AtAGlance_College: any = null;
	AtAGlance_Institution: any = null;
	AtAGlance_CSCDegree: any = null;
	AtAGlance_Podcast: any = null;
	AtAGlance_PodcastEpisode: any = null;
	AtAGlance_Training: any = null;
	AtAGlance_Certification: any = null;
	AtAGlance_Resource: any = null;
	Institutions: any = null;
	Folder: IFolder = null;
	AtAGlance_Military: IAtAGlanceMilitary = null;
}

@Injectable({
	providedIn: 'root'
})
export class TerritoryCardService {
	constructor(
		private appService: AppService,
		private mapAPI: MapAPIService,
		private resourceAPI: ResourceAPIService,
		private router: Router,
	) { }

	defaultColor = {
		backgroundColor: 'grey',
		foregroundColor: 'black'
	};

	getColors(cardType: ETerritoryCardType) {
		switch (cardType) {
			case ETerritoryCardType.Career:
				return {
					backgroundColor: '#ecfeca',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.Certification:
				return {
					backgroundColor: '#D0E9EB',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.CTE:
				return {
					backgroundColor: '#C6FBE3',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.Military:
				return {
					backgroundColor: '#ECFECA',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.College:
				return {
					backgroundColor: '#F8D5C1',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.K12:
				return {
					backgroundColor: '#C6FBE3',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.PreK:
				return {
					backgroundColor: '#EFE4F4',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.Resource:
				return {
					backgroundColor: '#ecfeca',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.Training:
			case ETerritoryCardType.Institution:
				return {
					backgroundColor: '#F8D5C1',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.FieldsOfStudy:
				return {
					backgroundColor: '#F8D5C1',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.CSCDegree:
				return {
					backgroundColor: '#F8D5C1',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.Podcast:
			case ETerritoryCardType.PodcastEpisode:
				return {
					backgroundColor: '#ecfeca',
					foregroundColor: '#988060'
				};
			case ETerritoryCardType.NCES:
				return {
					backgroundColor: 'white',
					foregroundColor: 'black'
				};
			case ETerritoryCardType.Folder:
				return {
					backgroundColor: 'white',
					foregroundColor: 'black'
				};
			case ETerritoryCardType.ProfessionalAssociation:
				//place holder
				return {
					backgroundColor: 'white',
					foregroundColor: 'black'
				};
			default:
				return this.defaultColor;
		}
	};

	sortByTopDegree(edu:any) : any {
		var eduToSort: any[] = [];
		var index = 0;
		edu.foreach(
			function (value: any, key: any) {
				eduToSort[index] = [value, key];
				index++;
			}
		);
		eduToSort.sort(function (left, right) {
			return left[0] > right[0] ? -1 : 1;
		});
		return eduToSort;
	};

	degreeNames =
	{
		'AssociateDegreePercent': 'Associate\'s degree',
		'BachelorDegreePercent': 'College (4 Year)',
		'DoctoralDegreePercent': 'Doctoral degree',
		'HighSchoolDiplomaPercent': 'High school',
		'MasterDegreePercent': 'Master\'s degree',
		'PostBaccalaureateCertificatePercent': 'Post-baccalaureate certificate',
		'PostDoctoralTrainingPercent': 'Post-baccalaureate certificate',
		'PostMasterCertificatePercent': 'Post-master certificate',
		'PostSecondaryCertificatePercent': 'Professional degree certificate',
		'ProfessionalDegreePercent': 'Professional degree',
		'SomeCollegeNoDegreePercent': 'Some college',
		'UnderHighSchoolPercent': 'No high school diploma'
	};

	//#region cyber
	// podcast
	normalizePodcast(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.Podcast;
		n.HasLearnMore = true;
		n.learnMoreType = ETerritoryCardLearnMoreType.PodcastEpisode;
		n.hasAtAGlance = true;
		n.CanSocial = true;
		n.socialSource = ESocialSourceType.Cyber;
		n.socialType = ESocialType.CyberPodcast;
		n.socialKey = obj.PodcastId;
		n.socialIcon = ESocialIconType.Favorite;
		n.objId = obj.PodcastId;
		n.Name = obj.PodcastName;
		n.HeroImage = obj.Image;
		n.AtAGlance_Podcast = {
			ArtistName: obj.ArtistName,
			ReleaseDate: new Date(obj.ReleaseDate).toLocaleString(),
		}
		return n;
	};

	// podcast episode
	normalizePodcastEpisode(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.PodcastEpisode;
		n.learnMoreType = ETerritoryCardLearnMoreType.Audio;
		n.hasAtAGlance = true;
		n.CanSocial = true;
		n.socialSource = ESocialSourceType.Cyber;
		n.socialType = ESocialType.CyberPodcastEpisode;
		n.socialKey = obj.EpisodeId;
		n.socialIcon = ESocialIconType.Favorite;
		n.objId = obj.EpisodeId;
		n.Name = obj.EpisodeName;
		n.Description = obj.Description;
		n.HeroImage = obj.Image;
		if (obj.EpisodeUrl.trim() !== "") {
			n.HasLearnMore = true;
			n.externalUrl = (obj.EpisodeUrl.indexOf("http") < 0 ? "https://" : "") + obj.EpisodeUrl.trim();
		}
		else {
			n.HasLearnMore = false;
			n.externalUrl = "";
		}
		n.AtAGlance_PodcastEpisode = {
			TrackTime: obj.TrackTime,
			ReleaseDate: new Date(obj.ReleaseDate).toLocaleString(),
			Rating: obj.Rating,
			EpisodeContentType: obj.EpisodeContentType,
			EpisodeFileExtension: obj.EpisodeFileExtension,
			EpisodeUrl: obj.EpisodeUrl
		}
		return n;
	};
	//#endregion cyber

	//#region college score card 
	normalizeInstitution(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.Institution;
		n.HasLearnMore = true;
		n.CanSocial = true;
		n.hasAtAGlance = true;
		n.Name = obj.Name;
		n.HeroImage = obj.SchoolLogo;
		// although college scorecard data is from external, the save function will re-get during save
		n.socialSource = ESocialSourceType.CollegeScorecard;
		n.socialType = ESocialType.CSCCollege;
		n.socialKey = obj.Id;
		n.objId = obj.Id;

		let classroom: string = "";
		let size: number = 0;
		if (obj.StudentSize != null) {
			size = parseInt(obj.StudentSize);
		}
		else if (obj.Student != null && obj.Student.StudentSize != null) {
			size = parseInt(obj.Student.StudentSize);
		}
		if (size < 2000) {
			classroom = "Small";
		}
		else if (size >= 2000 && size < 15000) {
			classroom = "Medium";
		}
		else {
			classroom = "Large";
		}

		n.AtAGlance_Institution = {
			SchoolYear: obj.SchoolYear,
			Locale: obj.Locale,
			Type: obj.Type,
			StudentSize: size==0? null: size,
			Classroom: classroom,
			spaceHolderImage: obj.SchoolLogo.substring(0, obj.SchoolLogo.lastIndexOf('/')) + '/bg-spaceholder.png'
		};

		return n;
	}

	// csc fields of study
	normalizeFieldsofStudy(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.FieldsOfStudy;
		n.hasSelect = true;
		n.hasAtAGlance = true;
		n.objId = obj.CIPCode;
		n.Description = obj.Description;
		n.OccupationCode = obj.DefaultOnetCode;
		n.CIPCode = obj.CIPCode;
		n.Name = obj.Title;
		n.HeroImage = obj.ImageUrl;
		n.hasMany = true;
		// reuse existing logic, have one cluster and put all degrees as pathway
		n.ObjectInstances = [];
		for (let i = 0; i < obj.Degrees.length; i++) {
			n.ObjectInstances.push(
				{
					ClusterId: obj.CIPCode,
					ClusterHeroImage: obj.ImageUrl,
					ClusterName: obj.Title,
					MapObjectId: obj.Degrees[i].MapObjectId,
					PathwayId: obj.Degrees[i].CIPCode + "^" + obj.Degrees[i].CredentialLevel,
					PathwayHeroImage: obj.Degrees[i].ImageUrl,
					PathwayName: obj.Degrees[i].Title,
					ClusterIcon: "",
					ClusterDescription: obj.Description,
					PathwayDescription: obj.Degrees[i].Description,
					CIPCode: obj.Degrees[i].CIPCode,
					NumberOfGraduation: obj.Degrees[i].NumberOfGraduation,
					MedianTotalDebt: obj.Degrees[i].MedianTotalDebt,
					MedianEarnings: obj.Degrees[i].MedianEarnings,
					MonthlyLoanPayment: obj.Degrees[i].MonthlyLoanPayment,
					MonthlyEarnings: obj.Degrees[i].MonthlyEarnings
				}
			);
		}
		//at a glance
		n.AtAGlance_CSCDegree = {
			NumberOfGraduation: obj.NumberOfGraduation,
			MedianTotalDebt: obj.MedianTotalDebt,
			MedianEarnings: obj.MedianEarnings,
			MonthlyLoanPayment: obj.MonthlyLoanPayment,
			MonthlyEarnings: obj.MonthlyEarnings
		};

		return n;
	};

	// csc degree
	normalizeCSCDegree(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.CSCDegree;
		n.HasLearnMore = true;
		n.disableLearnMore = (obj.MapObjectId ?? "").toString().trim() === '' ? true : false;
		n.hasAtAGlance = true;
		n.objId = obj.Id;
		n.Name = obj.Name;
		n.Description = obj.Description;
		n.MapObjectId = obj.MapObjectId;
		n.HeroImage = obj.HeroImage;
		n.CIPCode = obj.CIPCode;
		//at a glance
		n.AtAGlance_CSCDegree = {
			NumberOfGraduation: obj.NumberOfGraduation,
			MedianTotalDebt: obj.MedianTotalDebt,
			MedianEarnings: obj.MedianEarnings,
			MonthlyLoanPayment: obj.MonthlyLoanPayment,
			MonthlyEarnings: obj.MonthlyEarnings
		};

		return n;
	};
	//#endregion college score card

	normalizeCTE(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.CTE;
		n.HasLearnMore = obj.HasLearnMore;
		n.Journeyable = obj.Journeyable;
		n.onMap = true;
		n.hasAtAGlance = true;
		n.Description = obj.CTEName;
		n.MapObjectId = obj.CTEId;
		n.objId = obj.CTEId;
		n.Name = obj.CTEName;
		n.TerritoryId = 3; //parseInt(obj.TerritoryLevel.toString().split('.')[0]);
		n.HeroImage = obj.HeroImage;
		n.Depth = obj.Depth;
		//needed for follow icon
		n.CanSocial = obj.CanSocial;
		n.socialSource = ESocialSourceType.Map;
		n.socialKey = obj.CTEId;
		//at a glance
		n.ClusterTotal = obj.ClusterTotal;     
		n.PathwayTotal = obj.PathwayTotal;      
		n.StandardTotal = obj.StandardTotal;    
		n.StrandTotal = obj.StrandTotal;       
		n.PerformanceIndicator = obj.PerformanceIndicator; 
		return n;
	};

	normalizeK12(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.K12;
		n.HasLearnMore = obj.HasLearnMore;
		n.Journeyable = obj.Journeyable;
		n.onMap = true;
		n.hasAtAGlance = true;
		n.Description = obj.K12Name;
		n.MapObjectId = obj.K12Id;
		n.objId = obj.K12Id;
		n.Name = obj.K12Name;
		n.TerritoryId = 6; 
		n.HeroImage = obj.HeroImage;
		n.Depth = obj.Depth;
		//needed for follow icon
		n.CanSocial = obj.CanSocial;
		n.socialSource = ESocialSourceType.Map;
		n.socialKey = obj.K12Id;
		//at a glance 
		n.SubjectsTotal = obj.SubjectsTotal; 
		n.StrandTotal = obj.StrandTotal; 
		n.StandardTotal = obj.StandardTotal; 
		n.Benchmark = obj.Benchmark; 
		n.SubBenchmark = obj.SubBenchmark; 
		return n;
	};

	//#region certification
	normalizeCertification(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();		
		n.tCardType = ETerritoryCardType.Certification;
		if (obj.Ancestory) {
			n.ancestoryBreadCrumb = obj.Ancestory;
		}
		n.HasLearnMore = obj.HasLearnMore;
		n.Journeyable = obj.Journeyable;
		n.onMap = true;
		n.hasAtAGlance = true;
		n.Description = obj.Description;
		n.MapObjectId = obj.CertificationId;
		n.objId = obj.CertificationId;
		n.Name = obj.CertificationName;
		if (obj.CertificateName !== undefined) {
			n.Name = obj.CertificateName;
		}
		n.TerritoryId = parseInt(obj.TerritoryLevel.toString().split('.')[0]);
		n.HeroImage = obj.HeroImage;
		n.Depth = obj.Depth;
		//needed for follow icon
		n.CanSocial = obj.CanSocial;
		n.socialSource = ESocialSourceType.Map;
		n.socialKey = obj.CertificationId;
		//at a glance
		if (obj.ClusterTotal !== undefined) {
			n.ClusterTotal = AppUtilityService.toCommaNumber(obj.ClusterTotal);
		}
		//level 2, 4, 5
		if (obj.PathwayTotal !== undefined) {
			n.PathwayTotal = AppUtilityService.toCommaNumber(obj.PathwayTotal);
		}

		n.OccupationTotal = AppUtilityService.toCommaNumber(obj.OccupationTotal);

		n.AtAGlance_Certification = {
			CertifyingOrganizationTotal: AppUtilityService.toCommaNumber(obj.CertifyingOrganizationTotal),
			CertificateTotal: AppUtilityService.toCommaNumber(obj.CertificateTotal),
			TrainingTotal: AppUtilityService.toCommaNumber(obj.TrainingTotal),
			ResourceTotal: AppUtilityService.toCommaNumber(obj.ResourceTotal),
			EducationTrainingRequired: AppUtilityService.toYesNo(obj.EducationTrainingRequired),
			WorkExperienceRequired: AppUtilityService.toYesNo(obj.WorkExperienceRequired),
			OralWrittenExamRequired: AppUtilityService.toYesNo(obj.OralWrittenExamRequired),
			CertificateRenewal: obj.RenewalRequired != null ? "Every " + obj.RenewalRequired + " Year(s)" : "N/A",
			CertificateReexam: AppUtilityService.toYesNo(obj.CertificateReexam),
			OrgName: obj.OrgName,
			OrgUrl: obj.OrgUrl,
			CertificateUrl: obj.CertificateUrl
		};

		if (obj.LevelIconClusters != null && obj.LevelIconClusters.length != null) {
			n.ancestoryIcons = obj.LevelIconClusters;
		}
		if (obj.MapInstances != null) {
			n.ObjectInstances = JSON.parse(JSON.stringify(obj.MapInstances));
			delete obj.MapInstances;
		}
		/*determine can social, do not do social if more than once instances*/
		n.hasMany = ((n.ObjectInstances?.length ?? 0) > 1) ? true : false;
		if (n.CanSocial != null && n.hasMany === true) {
			n.CanSocial = false;
		}
		if (n.ancestoryIcons !== null && n.ObjectInstances !== null) {
			if (n.ancestoryIcons.length === 1) {
				if (n.ObjectInstances.length === 1)
					n.ancestoryIconTitle = "Certificate found in 1 cluster(s)";
				else
					n.ancestoryIconTitle = "Certificate found in " + n.ObjectInstances.length + " pathway(s)";
			}
			else {
				n.ancestoryIconTitle = "Certificate found in " + n.ancestoryIcons.length + " cluster(s)";
			}
		}
		return n;
	};

	normalizeTraining(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.Training;
		if (obj.SchoolUrl.trim() !== "") {
			n.HasLearnMore = true;
			n.externalUrl = (obj.SchoolUrl.indexOf("http") < 0 ? "https://" : "") + obj.SchoolUrl.trim();
		}
		else {
			n.HasLearnMore = false;
			n.externalUrl = "";
		}
		n.MapObjectId = obj.CareerOnestopTrainingId;
		n.objId = obj.CareerOnestopTrainingId;
		n.Name = obj.SchoolName + ", " + obj.ProgramName;
		n.HeroImage = obj.SchoolLogo;

		n.hasAtAGlance = true;
		n.AtAGlance_Training = {
			OriginalData: obj,
			SchoolName: obj.SchoolName,
			City: obj.City,
			State: obj.State,
			ZipCode: obj.ZipCode,
			Phone: AppUtilityService.toPhoneNumber(obj.Phone),
			ProgramLength: obj.ProgramLength
		};
		if (obj.CareerOnestopTrainingId == null || obj.CareerOnestopTrainingId === "") {
			n.CanSocial = false;
		}
		else {
			n.CanSocial = true;
			n.socialSource = ESocialSourceType.Map;
			n.socialType = ESocialType.CertificateTraining;
			n.socialKey = obj.CareerOnestopTrainingId;
		}
		return n;
	}

	normalizeCertificateOrg(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.CertificateOrg;
		n.objId = obj.OrgId;
		n.Name = obj.OrgName;
		n.HeroImage = obj.HeroImage;
		n.CanSocial = true;
		n.socialSource = ESocialSourceType.CareerOneStopOrg;
		n.socialType = ESocialType.CertificateOrg;
		n.socialKey = obj.OrgId;
		n.socialObject = JSON.parse(JSON.stringify(obj));

		if (obj.OrgUrl.trim() !== "") {
			n.HasLearnMore = true;
			n.externalUrl = (obj.OrgUrl.indexOf("http") < 0 ? "https://" : "") + obj.OrgUrl.trim();
		}
		else {
			n.HasLearnMore = false;
			n.externalUrl = "";
		}
		return n;
	}
	//#endregion certification

	normalizeCollege(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.College;
		if (obj.Ancestory) {
			n.ancestoryBreadCrumb = obj.Ancestory;
		}
		n.HasLearnMore = obj.HasLearnMore;
		n.Journeyable = obj.Journeyable;
		n.socialSource = ESocialSourceType.Map;
		n.onMap = true;
		n.hasAtAGlance = true;
		n.Description = obj.Description;
		n.MapObjectId = obj.CollegeObjectId;
		n.objId = obj.CollegeObjectId;
		n.Name = obj.Label;
		n.TerritoryId = parseInt(obj.TerritoryLevelId.toString().split('.')[0]);
		n.HeroImage = obj.HeroImage;
		n.CanSocial = true;
		n.socialKey = obj.CollegeObjectId;
		n.Depth = obj.Depth;
		n.CIPCode = obj.CIPCode !== undefined? obj.CIPCode : "";
		
		//at a glance
		if (obj.ClusterTotal !== undefined) {
			n.ClusterTotal = AppUtilityService.toCommaNumber(obj.ClusterTotal);
		}
		if (obj.ProgramTotal !== undefined) {
			n.ProgramTotal = AppUtilityService.toCommaNumber(obj.ProgramTotal);
		}
		if (obj.CourseTotal !== undefined) {
			n.CourseTotal = AppUtilityService.toCommaNumber(obj.CourseTotal);
		}
		if (obj.AverageAnnualSalary !== undefined) {
			n.AverageAnnualSalary = AppUtilityService.toDollar(obj.AverageAnnualSalary, true);
		}
		if (obj.OccupationTotal !== undefined) {
			n.OccupationTotal = AppUtilityService.toCommaNumber(obj.OccupationTotal);
		}
		if (obj.ClassLength !== undefined) {
			n.ClassLength = obj.ClassLength;
		}
		if (obj.CreditTotal !== undefined) {
			n.CreditTotal = obj.CreditTotal;
		}
		if (obj.ProfessorTotal !== undefined) {
			if (obj.ProfessorTotal === "" || obj.ProfessorTotal === "0") {
				n.ProfessorTotal = null;
			}
			else {
				n.ProfessorTotal = AppUtilityService.toCommaNumber(obj.ProfessorTotal);
			}
		}
		if (n.Depth === 3) {
			n.AtAGlance_College = {
				MedianEarnings: (obj.MedianEarnings ?? null) != null ? AppUtilityService.toCommaNumber(obj.MedianEarnings) : null,
				MedianTotalDebt: (obj.MedianTotalDebt ?? null) != null ? AppUtilityService.toCommaNumber(obj.MedianTotalDebt) : null,
				MonthlyEarnings: (obj.MonthlyEarnings ?? null) != null ? AppUtilityService.toCommaNumber(obj.MonthlyEarnings) : null,
				MonthlyLoanPayment: (obj.MonthlyLoanPayment ?? null) != null ? AppUtilityService.toCommaNumber(obj.MonthlyLoanPayment) : null,
				NumGraduates: (obj.NumGraduates ?? null) != null ? AppUtilityService.toCommaNumber(obj.NumGraduates) : null
			}
		}
		return n;
	};

	normalizeNCES(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.NCES;
		n.objId = obj.NCESSCH;
		n.Name = obj.Name;
		n.HeroImage = obj.CoverImage;

		return n;
	}

	normalizeResource(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.Resource;
		if (obj.Url.trim() !== "") {
			n.HasLearnMore = true;
			n.externalUrl = (obj.Url.indexOf("http") < 0 ? "https://" : "") + obj.Url.trim();
		}
		else {
			n.HasLearnMore = false;
			n.externalUrl = "";
		}
		n.objId = obj.ResourceId;
		n.Name = obj.Title;
		if (obj.CanSocial != undefined && obj.CanSocial != null) {
			n.CanSocial = obj.CanSocial
		}
		else {
			n.CanSocial = true;
		}
		n.socialSource = ESocialSourceType.Resource;
		n.socialType = ESocialType.Resource;
		n.socialKey = obj.ResourceId;
		n.socialObject = JSON.parse(JSON.stringify(obj));
		n.HeroImage = obj.ThumbnailUrl;
		n.AtAGlance_Resource = {
			CanEdit: false,
			SetVisibility: false,
			Share: false,
			Priority: false,
			IsFile: false,
			PersonRole: '-1',
			PersonId: '',
			CreatedTime: ''
		};
		if (obj.ResourceFrom != undefined && obj.ResourceFrom != null) {
			n.AtAGlance_Resource.CanEdit = !n.CanSocial;
			n.AtAGlance_Resource.Share = obj.Share;
			n.AtAGlance_Resource.Priority = obj.Priority;
			n.AtAGlance_Resource.IsFile = obj.IsFile;
			n.AtAGlance_Resource.PersonRole = obj.PersonRole;
			n.AtAGlance_Resource.CreatedTime = obj.CreatedTime;
			n.AtAGlance_Resource.PersonId = obj.PersonId;
			this.resourceAPI.getRolePermit().subscribe({
				next: (response: IRolePermit) => {
					n.AtAGlance_Resource.SetVisibility = response.CanShare;
				}
			});
		}
		if (obj.Description) {
			n.Description = obj.Description;
		} else {
			n.Description = "";
		}
		return n;
	}

	// please do not directly use the data returned from API.
	// The return data type of normalize function should be same as others: TerritoryCardData
	normalizeCareer(obj: any): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.Career;

		if (obj.TerritoryId == null) {
			if (obj.TerritoryLevelId != null) {
				n.TerritoryId = parseInt(obj.TerritoryLevelId.split('.')[0]);
			}
		}
		else {
			n.TerritoryId = obj.TerritoryId;
		}

		if(n.TerritoryId == null || n.TerritoryId !== 1) {
			throw "This function is only for career on map";
		}

		if (obj.MapObjectId == null) {
			if (obj.CareerId != null) {
				n.MapObjectId = obj.CareerId;
			}
		}
		else {
			n.MapObjectId = obj.MapObjectId;
		}
		n.objId = n.MapObjectId;
		if (obj.Depth != null) {
			n.Depth = obj.Depth;
		}
		else if (obj.DepthLevel) {
			n.Depth = obj.DepthLevel;
		}

		n.Description = obj.Description ?? "";
		n.OccupationCode = obj.OccupationCode ?? "";
		n.HasLearnMore = obj.HasLearnMore;
		n.Journeyable = obj.Journeyable;
		n.CanSocial = obj.CanSocial;
		n.HeroImage = obj.HeroImage ?? "";
		n.AverageAnnualSalary = obj.AverageAnnualSalary ?? null;
		n.OccupationTotal = obj.OccupationTotal ?? null;
		n.SkillTotal = obj.SkillTotal ?? null;
		n.AbilityTotal = obj.AbilityTotal ?? null;
		n.PathwayTotal = obj.PathwayTotal ?? null;
		n.KnowledgeTotal = obj.KnowledgeTotal ?? null;
		n.TechnologyTotal = obj.TechnologyTotal ?? null;
		n.TaskTotal = obj.TaskTotal ?? null;
		n.WorkActivitiyTotal = obj.WorkActivitiyTotal ?? null;
		n.Education = obj.Education ?? null;
		n.ProjectedEmploymentGrowthAverage = obj.ProjectedEmploymentGrowthAverage ?? null;
		n.ProjectedEmploymentGrowthPercent = obj.ProjectedEmploymentGrowthPercent ?? null;
		n.ProjectedJobOpenings = obj.ProjectedJobOpenings != null && String(obj.ProjectedJobOpenings).trim() !== "" && !isNaN(obj.ProjectedJobOpenings) ? parseInt(obj.ProjectedJobOpenings ?? null).toString() : "";
		n.CurrentEmployment = obj.CurrentEmployment ?? null;

		if ((obj.OccupationName !== undefined && obj.OccupationName != null) || (obj.Name !== undefined && obj.Name != null) || (obj.CareerName !== undefined && obj.CareerName != null)) {
			if (obj.OccupationName !== undefined && obj.OccupationName != null) {
				n.Name = obj.OccupationName;
			}
			else if (obj.Name !== undefined && obj.Name != null) {
				n.Name = obj.Name;
			}
			else if (obj.CareerName !== undefined && obj.CareerName != null) {
				n.Name = obj.CareerName;
			}
			else {
				n.Name = null;
			}
		}

		//this block lets the territory card determine which level icon to show: cluster or pathway
		if (obj.LevelIconClusters != null && obj.LevelIconClusters.length != null) {
			n.ancestoryIcons = obj.LevelIconClusters;
		}
		else if (obj.LevelIconPathway !== "" && obj.LevelIconPathway !== undefined && obj.LevelIconPathway != null) {
			n.ancestoryIcons = obj.LevelIconPathway;
		}
		else {
			n.ancestoryIcons = null;
		}

		//append follow data for social icon directive
		if (n.CanSocial && n.MapObjectId != null) {
			n.socialKey = n.MapObjectId;
			n.socialSource = ESocialSourceType.Map;
			n.socialIcon = ESocialIconType.Follow;
		}

		n.onMap = true;
		n.hasAtAGlance = true;
		n.hasVideo = (n.Depth ?? 0) === 4 ? true : false;

		//determine can social, do not do social if more than once instances
		if (obj.MapInstances != null) {
			n.ObjectInstances = JSON.parse(JSON.stringify(obj.MapInstances));
		}
		n.hasMany = (n.ObjectInstances?.length??0) > 1 ? true : false;
		if (n.CanSocial === true && n.hasMany === true) {
			n.CanSocial = false;
		}

		if (n.ObjectInstances != null) {
			if (n.ObjectInstances.length === 1)
				n.ancestoryIconTitle = "Occupation found in 1 cluster(s)";
			else
				n.ancestoryIconTitle = "Occupation found in " + n.ObjectInstances.length + " pathway(s)";
		}

		n.learnMoreType = ETerritoryCardLearnMoreType.LearnMore;
		return n;
	}

	normalizeFolder(folder: IFolder, canDelete: boolean = false): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.Folder;		
		n.objId = folder.FolderId;
		n.Name = folder.FolderName;
		n.HeroImage = (folder.ContainsNumTopLevelFolders > 0 || folder.ContainsNumTopLevelFiles > 0) ? 'assets/images/mobile_card_folder_open_hero.png':'assets/images/mobile_card_folder_hero.png'; //only used on mobile card view
		n.Folder = {
			FolderId: folder.FolderId,			
			FolderName: folder.FolderName,
			CreatedDate: folder.CreatedDate,
			CanDelete: canDelete,
			ContainsItems: false,
			ContainsNumTopLevelFolders: folder.ContainsNumTopLevelFolders,
			ContainsNumTopLevelFiles: folder.ContainsNumTopLevelFiles
		}
		return n;
	}

	normalizeProfessionalAssociation(professionalAssociation: IProfessionalAssociation): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.ProfessionalAssociation;
		n.Name = professionalAssociation.Name;
		n.HeroImage = professionalAssociation.HeroImage;
		n.externalUrl = professionalAssociation.Url;
		return n;
	}

	normalizeMilitary(obj: IMilitary): ITerritoryCard {
		let n: ITerritoryCard = new TerritoryCardData();
		n.tCardType = ETerritoryCardType.Military;
		n.MapObjectId = obj.MilitaryId;
		n.Name = obj.MilitaryName;
		n.Depth = obj.Depth;
		n.Description = obj.Description;
		n.Journeyable = obj.Journeyable;
		n.CanSocial = obj.CanSocial;
		if (n.CanSocial) {
			n.socialSource = ESocialSourceType.Map;
			n.socialType = ESocialType.Military;
			n.socialKey = obj.MilitaryId;
		}
		n.HasLearnMore = obj.HasLearnMore;
		n.HeroImage = obj.ImageUrl;
		n.onMap = true;
		n.learnMoreType = ETerritoryCardLearnMoreType.LearnMore;
		if(obj.MapInstances != null) {
			n.ObjectInstances = JSON.parse(JSON.stringify(obj.MapInstances));
			if (n.ObjectInstances?.length > 0) {
				n.hasMany = n.ObjectInstances.length > 1;
				n.ancestoryIcons = n.ObjectInstances.map((i) => i.ClusterIcon);
				if (n.ancestoryIcons.length === 1) {
					if (n.ObjectInstances.length === 1)
						n.ancestoryIconTitle = "Military found in 1 cluster(s)";
					else
						n.ancestoryIconTitle = "Military found in " + n.ObjectInstances.length + " pathway(s)";
				}
				else {
					n.ancestoryIconTitle = "Military found in " + n.ancestoryIcons.length + " cluster(s)";
				}
			}
		}
		n.hasAtAGlance = true;
		n.AtAGlance_Military = {
			Branch: obj.Branch,
			MOSCode: obj.MOSCode,
			MCId: obj.MCId == null || obj.MCId.length == 0 ? "" : obj.MCId,
			BranchIcon: MapService.militaryClusterImageSelector(obj.Branch),
			MOSUrl: obj.MOSCode == null || obj.MOSCode.length == 0 ? "" : "https://www.mynextmove.org/vets/find/military?b=&s=" + obj.MOSCode,
			MCUrl: obj.MCId == null || obj.MCId.length == 0 ? "" : "https://www.careersinthemilitary.com/career-detail/" + obj.MCId,  
		}
		return n;
	}

	go(objectId: string) {
		this.mapAPI.getMapObject(objectId).subscribe({
			next: (data: any) => {
				let territoryId = data.TerritoryId;

				if (territoryId === 1) {
					if (this.appService.fromEmbedOccupations) {
						window.open(AppSettingService.baseUrl + "/Career/LearnMore/" + encodeURIComponent(objectId));
					}
					else {
						this.router.navigateByUrl("Career/LearnMore/" + objectId);
					}
				}
				else if (territoryId === 2) {
					if (this.appService.fromEmbedOccupations) {
						window.open(AppSettingService.baseUrl + "/Certification/LearnMore/" + encodeURIComponent(objectId));
					}
					else {
						this.router.navigateByUrl("Certification/LearnMore/" + objectId);
					}
				}
				else if (territoryId === 3) {
					if (this.appService.fromEmbedOccupations) {
						window.open(AppSettingService.baseUrl + "/CTE/LearnMore/" + encodeURIComponent(objectId));
					}
					else {
						this.router.navigateByUrl("CTE/LearnMore/" + objectId);
					}
				}
				else if (territoryId === 4) {
					if (this.appService.fromEmbedOccupations) {
						window.open(AppSettingService.baseUrl + "/Military/LearnMore/" + encodeURIComponent(objectId));
					}
					else {
						this.router.navigateByUrl("Military/LearnMore/" + objectId);
					}
				}
				else if (territoryId === 5) {
					if (this.appService.fromEmbedOccupations) {
						window.open(AppSettingService.baseUrl + "/College/LearnMore/" + encodeURIComponent(objectId));
					}
					else {
						this.router.navigateByUrl("College/LearnMore/" + objectId);
					}
				}
				else if (territoryId === 6) {
					if (this.appService.fromEmbedOccupations) {
						window.open(AppSettingService.baseUrl + "/K12/LearnMore/" + encodeURIComponent(objectId));
					}
					else {
						this.router.navigateByUrl("K12/LearnMore/" + objectId);
					}
				}
			},
			error: (err: any) => {
				console.log(err);
				alert("The given object is not in map");
			}
		});
	}
}
